import { $host } from './index';
import { AppDispatch } from '@store';
import { isSpinAC, notificationModalAC, otpLifeTimeAC } from '@store/reducers/app';
import { pickedSalaryInfoAC, salaryListAC, verifyFileIdAC } from '@store/reducers/salary';
import { otpConfirmAC } from '@store/reducers/transactions';

const END_POINT = '/client';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const getErrorMessage = (error: any) => {
  let data = error?.response?.data;

  if (data instanceof ArrayBuffer) {
    try {
      data = JSON.parse(new TextDecoder('utf-8').decode(new Uint8Array(data)));
    } catch (e) {
      data = null;
    }
  }

  return data?.detail || data?.message;
};

const showError = (dispatch: AppDispatch, error: any) => {
  dispatch(
    notificationModalAC({
      isOpen: true,
      message: getErrorMessage(error),
      type: 'error',
    })
  );
};

const saveFile = (data: any, fileName: string, type: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
};

const getSalaryTemplateApi = (callback: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.get(`${END_POINT}/salary/file/template`, {
      responseType: 'arraybuffer',
    });
    saveFile(data, 'Шаблон зарплат.xlsx', XLSX_MIME);
    callback();
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getSalaryListApi = (params: any, page: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/salary/files?page=${page}`, params);
    dispatch(salaryListAC(data));
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

const refreshSalaryListApi = (params: any, page: any) => async (dispatch: AppDispatch) => {
  try {
    const { data } = await $host.post(`${END_POINT}/salary/files?page=${page}`, params);
    dispatch(salaryListAC(data));
  } catch (error: any) {
    showError(dispatch, error);
  }
};

const uploadSalaryFileApi = (file: any, callback: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await $host.post(`${END_POINT}/salary/file/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (data) {
      callback();
    }
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

interface ConfirmSalaryFileHandlers {
  onOtpRequired?: () => void;
  onDone?: () => void;
}

const confirmSalaryFileApi = (params: any, handlers?: ConfirmSalaryFileHandlers) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/salary/file/upload/confirm`, params);

    if (data?.otp_required) {
      dispatch(otpConfirmAC(true));
      dispatch(verifyFileIdAC(data?.file_id));
      dispatch(otpLifeTimeAC(Date.now() + 1000 * data.code_lifetime));
      handlers?.onOtpRequired?.();
    } else {
      dispatch(otpConfirmAC(false));
      dispatch(verifyFileIdAC(''));
      handlers?.onDone?.();
    }
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getSalaryInfoApi = (params: any, page: any, callback?: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data, status } = await $host.post(`${END_POINT}/salary/files/trns?page=${page}`, params);
    if (data) {
      dispatch(pickedSalaryInfoAC(data));
      if (callback) {
        callback();
      }
    }
    return status;
  } catch (error: any) {
    showError(dispatch, error);
    return error?.response?.status as number | undefined;
  } finally {
    dispatch(isSpinAC(false));
  }
};

const refreshSalaryInfoApi = (params: any, page: any, callback?: any) => async (dispatch: AppDispatch) => {
  try {
    const { data } = await $host.post(`${END_POINT}/salary/files/trns?page=${page}`, params);
    if (data) {
      dispatch(pickedSalaryInfoAC(data));
      if (callback) {
        callback();
      }
    }
  } catch (error: any) {
    showError(dispatch, error);
  }
};

const dowloadOriginalSalaryFileApi = (params: object) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/salary/file/download`, params, {
      responseType: 'arraybuffer',
    });
    saveFile(data, 'Файл зарплаты.xlsx', XLSX_MIME);
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

const downloadSalaryExtractFileApi = (params: object) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/salary/file/stat/download`, params, {
      responseType: 'arraybuffer',
    });
    saveFile(data, 'выписка зарплаты.pdf', 'application/pdf');
  } catch (error: any) {
    showError(dispatch, error);
  } finally {
    dispatch(isSpinAC(false));
  }
};

export {
  refreshSalaryInfoApi,
  refreshSalaryListApi,
  getSalaryTemplateApi,
  uploadSalaryFileApi,
  getSalaryListApi,
  confirmSalaryFileApi,
  getSalaryInfoApi,
  dowloadOriginalSalaryFileApi,
  downloadSalaryExtractFileApi,
};
